import { useMutation } from '@apollo/client';
import { Box, Flex } from '@theme-ui/components';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from '@app/components/Button';
import Checkbox from '@app/components/input/Checkbox';
import { H2 } from '@app/components/typography';
import useRouterPush from '@app/hooks/useRouterPush';
import { closeModal, openModal } from '@app/redux/modal';

import useSimpleCart from '@app/hooks/useSimpleCart';
import {
  AddressFormFields,
  AddressFormNamespaces,
  FORM_REVALIDATE_MODE,
} from './Address.constants';
import messages from './Address.messages';
import { SET_BILLING_ADDRESS, SET_SHIPPING_ADDRESS } from './Address.queries';
import type { AddressFormState } from './Address.types';
import { FormVisibilityState } from './Address.types';
import AddressForm from './AddressForm/AddressForm';
import CustomerAddresses from './CustomerAddresses/CustomerAddresses';
import { ADD_ADDRESS } from './CustomerAddresses/CustomerAddresses.queries';
import EditAddressForm from './CustomerAddresses/EditAddressForm';
import type {
  CreateCustomerAddressMutation,
  CreateCustomerAddressMutationVariables,
} from './CustomerAddresses/__generated__/CreateCustomerAddressMutation';
import {
  hasValidPhone,
  setBackendValidationError,
} from './CustomerAddresses/helpers';
import type {
  SetBillingAddress,
  SetBillingAddressVariables,
} from './__generated__/SetBillingAddress';
import type {
  SetShippingAddress,
  SetShippingAddressVariables,
} from './__generated__/SetShippingAddress';
import { mapToNewCustomerAddress } from './helpers';

const ShippingBillingAddress: FC<{ cartId: string }> = ({ cartId }) => {
  const { isVirtual } = useSimpleCart();
  const dispatch = useDispatch();
  const form = useForm<AddressFormState>({
    reValidateMode: FORM_REVALIDATE_MODE,
  });
  const push = useRouterPush();
  const intl = useIntl();

  const { setError, register } = form;

  const [
    createCustomerAddress,
    { loading: isCustomerAddressSending, error: createCustomerAddressError },
  ] = useMutation<
    CreateCustomerAddressMutation,
    CreateCustomerAddressMutationVariables
  >(ADD_ADDRESS);

  const [setShippingAddress, { loading: isShippingAddressSending }] =
    useMutation<SetShippingAddress, SetShippingAddressVariables>(
      SET_SHIPPING_ADDRESS,
    );

  const [setBillingAddress, { loading: isBillingAddressSending }] = useMutation<
    SetBillingAddress,
    SetBillingAddressVariables
  >(SET_BILLING_ADDRESS);

  const sameAsShipping = useWatch({
    control: form.control,
    name: 'sameAsShipping',
    defaultValue: true,
  });

  const formVisibility = useWatch({
    control: form.control,
    name: AddressFormFields.FormVisibility,
  });

  const isLoading =
    isShippingAddressSending ||
    isBillingAddressSending ||
    isCustomerAddressSending;

  useEffect(() => {
    if (createCustomerAddressError) {
      const namespace =
        formVisibility === FormVisibilityState.SHOWN
          ? AddressFormNamespaces.Shipping
          : AddressFormNamespaces.Billing;

      setBackendValidationError(
        namespace,
        createCustomerAddressError,
        setError,
      );
    }
  }, [createCustomerAddressError, formVisibility, setError]);

  const submit = useCallback(
    async ({
      shipping: newShipping,
      selected: selectedShipping,
      billing: newBilling,
      sameAsShipping,
    }: AddressFormState) => {
      if (selectedShipping && !hasValidPhone(selectedShipping)) {
        setError(AddressFormFields.Selected, {
          type: 'manual',
          message: intl.formatMessage(messages.phoneMissingError),
        });

        return;
      }

      let customerShippingAddressId: number | null | undefined =
        selectedShipping?.id;

      if (!customerShippingAddressId && newShipping) {
        const newCustomerShippingAddress = mapToNewCustomerAddress(newShipping);

        const response = await createCustomerAddress({
          variables: {
            input: {
              ...newCustomerShippingAddress,
              default_shipping: true,
              default_billing: sameAsShipping,
            },
          },
        });

        customerShippingAddressId =
          response?.data?.magento2?.createCustomerAddress?.id;
      }

      if (sameAsShipping && customerShippingAddressId) {
        /** We use setBillingAddressMutation because it allows us to copy the data
         * to shipping address without the need of a new query (use_for_shipping).
         * However, we are passing the customerShippingAddressId, therefore it's storing
         * the shipping address data for both forms.
         */
        try {
          await setBillingAddress({
            variables: {
              cartId,
              billingAddressId: customerShippingAddressId,
              useForShipping: true,
            },
          });
          push('/:language/shop/checkout/delivery/');
        } catch {
          if (selectedShipping) {
            dispatch(
              openModal({
                label: 'edit-address',
                content: (
                  <EditAddressForm
                    selected={true}
                    cart={{ cartId: cartId || '', isVirtual: false }}
                    address={selectedShipping}
                    onClose={() => dispatch(closeModal())}
                    validateOnOpen={true}
                  />
                ),
              }),
            );
          }
          return;
        }
      } else {
        const newCustomerBillingAddress =
          newBilling && mapToNewCustomerAddress(newBilling);

        const response = await createCustomerAddress({
          variables: {
            input: {
              ...newCustomerBillingAddress,
              default_billing: true,
            },
          },
        });

        const customerBillingAddressId =
          response?.data?.magento2?.createCustomerAddress?.id;

        if (customerShippingAddressId && customerBillingAddressId) {
          await setShippingAddress({
            variables: { cartId, shippingAddressId: customerShippingAddressId },
          });

          await setBillingAddress({
            variables: { cartId, billingAddressId: customerBillingAddressId },
          });

          push('/:language/shop/checkout/delivery/');
        }
      }
    },
    [
      createCustomerAddress,
      setShippingAddress,
      cartId,
      setBillingAddress,
      setError,
      dispatch,
      push,
      intl,
    ],
  );

  return (
    <FormProvider {...form}>
      <H2 as="h1">
        <FormattedMessage {...messages.shippingAddressTitle} />
      </H2>

      <Flex my={20} sx={{ flexWrap: 'wrap' }}>
        <CustomerAddresses />

        <Box sx={{ width: '100%' }} mb={20}>
          {formVisibility === FormVisibilityState.SHOWN && (
            <AddressForm
              namespace={AddressFormNamespaces.Shipping}
              isVirtual={isVirtual}
            />
          )}
        </Box>

        <Box sx={{ width: '100%' }}>
          <H2 as="h1">
            <FormattedMessage {...messages.billingAddressTitle} />
          </H2>

          <Box my={20}>
            <label htmlFor="sameAsShipping">
              <Checkbox
                {...register('sameAsShipping')}
                data-testid="same-as-shipping-checkbox"
                type="checkbox"
                name="sameAsShipping"
                id="sameAsShipping"
                defaultChecked={true}
              />
              <span>
                <FormattedMessage {...messages.sameAsShipping} />
              </span>
            </label>
          </Box>
          <Box sx={{ width: '100%' }} mb={20}>
            {!sameAsShipping && (
              <AddressForm
                namespace={AddressFormNamespaces.Billing}
                hiddenFields={[AddressFormFields.VatNumber]}
                isVirtual={isVirtual}
              />
            )}
          </Box>

          <Flex sx={{ justifyContent: 'flex-end' }}>
            <Button
              data-testid="address-continue-button"
              type="primary"
              isLoading={isLoading}
              onClick={form.handleSubmit(submit)}
              disabled={isLoading}
            >
              <FormattedMessage {...messages.continueButtonLabel} />
            </Button>
          </Flex>
        </Box>
      </Flex>
    </FormProvider>
  );
};

export default ShippingBillingAddress;
